<template>
  <div class="term-row" :class="{ 'term-row-form': isEditable }">
    <div>
      <div class="term">
        <div class="term-item term-item-lg">{{ $t('general.date') }}:</div>
        <div v-if="!isEditable" class="term-value">{{ datePlan }}</div>
        <div v-else class="term-value term-value-input">
          <base-calendar-wrap :is-small="true">
            <date-input v-model="localTraining.date_plan" :min-date="currentDate"></date-input>
          </base-calendar-wrap>
        </div>
      </div>
      <div class="term">
        <div class="term-item term-item-lg">{{ $t('general.limits') }}:</div>
        <div v-if="!isEditable" class="term-value">
          <template v-if="localTraining.heart_rate_min">
            {{ +localTraining.heart_rate_min }}
            &ndash;
            {{ +localTraining.heart_rate_max }}
            {{ $t('unit.bpm') }}
          </template>
          <template v-else>{{ $t('src/components/training/trainingparametersform.2330') }}</template>
        </div>
        <div v-else>
          <base-checkbox name="checkbox-watch-hr" v-model="localWatchHr">{{
            $t('src/components/training/trainingparametersform.8')
          }}</base-checkbox>
          <keep-alive>
            <div v-if="localWatchHr">
              <base-range-two-inputs
                :start-value="heart_rate_min"
                :end-value="heart_rate_max"
                :min="minRate"
                :max="maxRate"
                :step="5"
                class="form-group-short"
                @input="changeRate"
                @set:interval="setInterval"
                ref="hr"
              ></base-range-two-inputs>
              <patient-max-hr :patient="patient"></patient-max-hr>
            </div>
          </keep-alive>
        </div>
      </div>
      <div class="term">
        <div class="term-item term-item-lg">{{ $t('general.duration') }}:</div>
        <template v-if="!isEditable">
          <span v-if="isDurationEmpty">—</span>
          <duration-with-unit
            v-else
            v-model="duration.value"
            :durationUnit="duration.unit"
            :is-readonly="true"
            @change:durationUnit="changeDurationUnit"
          ></duration-with-unit>
        </template>
        <div v-else>
          <base-checkbox name="checkbox-watch-duration" v-model="localWatchDuration">{{
            $t('src/components/training/trainingparametersform.9')
          }}</base-checkbox>
          <div v-if="localWatchDuration">
            <base-form-group
              :is-invalid="$v.duration.value.$invalid"
              :errors="{
                numeric: !$v.duration.value.numeric,
                minValue: !$v.duration.value.minValue && duration.value !== null,
                maxValue: !$v.duration.value.maxValue
              }"
              :messages="{
                minValue: $t('errorMessage.minDurationSession'),
                maxValue: $t('errorMessage.maxDurationSession')
              }"
            >
              <duration-with-unit
                v-model="duration.value"
                :durationUnit="duration.unit"
                @change:durationUnit="changeDurationUnit"
              ></duration-with-unit>
            </base-form-group>
          </div>
        </div>
      </div>
      <div class="term">
        <div class="term-item term-item-lg">
          {{ $t('src/components/training/trainingparametersform.5344') }}
        </div>
        <div v-if="!isEditable" class="term-value">{{ description }}</div>
        <div v-else class="term-value">
          <base-textarea v-model="localTraining.description" name="session-comment"></base-textarea>
        </div>
      </div>
      <button v-if="isEditable" type="button" class="btn btn-primary" @click="submit">
        <span v-if="saving" class="spinner-border spinner-border-sm"></span>{{ $t('button.save') }}
      </button>
    </div>
  </div>
</template>

<script>
import { DATE_FORMAT, HEART_RATE, TRAINING_TYPE, TRAINING_DEFAULT_COMMENTS } from '@/main.config.js'
import { required, numeric, minValue, maxValue, decimal } from 'vuelidate/lib/validators'
import { userRights } from '@/mixins/user-rights.js'
import { nowMoment } from '@/mixins/now-moment.js'
import { trainingDate } from '@/mixins/training-date'
import { patientMaxHR } from '@/mixins/patient-max-hr'
import { trainingItem } from '@/mixins/training-item'
import('@/styles/components/terms.less')

export default {
  name: 'TrainingParametersForm',
  components: {
    BaseCheckbox: () => import('@/base/BaseCheckbox'),
    BaseCalendarWrap: () => import('@/base/BaseCalendarWrap'),
    DateInput: () => import('@/plugins/DateInput.vue'),
    BaseRangeTwoInputs: () => import('@/base/BaseRangeTwoInputs.vue'),
    BaseTextarea: () => import('@/base/BaseTextarea.vue'),
    DurationWithUnit: () => import('@/components/DurationWithUnit.vue'),
    PatientMaxHr: () => import('@/components/PatientMaxHr.vue'),
    BaseFormGroup: () => import('@/base/BaseFormGroup.vue')
  },
  mixins: [userRights, nowMoment, trainingDate, patientMaxHR, trainingItem],
  props: {
    training: {
      type: Object,
      required: true
    },
    saving: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    localTraining: null,
    minRate: HEART_RATE.min,
    maxRate: HEART_RATE.max,
    localIs6MWT: false,
    localIsRoutine: false,
    localWatchHr: false,
    localWatchDuration: false,
    duration: null,
    heart_rate_max: null,
    heart_rate_min: null,
    watchInPause: false
  }),
  validations() {
    return {
      localTraining: {
        heart_rate_max: {
          required,
          numeric
        },
        heart_rate_min: {
          required,
          numeric
        },
        duration: {
          required,
          numeric
        },
        date_plan: {
          required
        }
      },
      duration: {
        value: {
          numeric: this.getType(),
          minValue: minValue(this.minValue),
          maxValue: maxValue(this.maxValue)
        }
      }
    }
  },
  computed: {
    patient() {
      return this.training.course.patient
    },
    isDurationEmpty() {
      return !this.localTraining.duration
    },
    isEditable() {
      return (
        this.isTrainingStatusPlan &&
        this.isDoctor &&
        (this.getTrainingMomentStartDate(this.training).isAfter(this.moment(), 'day') ||
          (this.getTrainingMomentStartDate(this.training).isSame(this.moment(), 'day') &&
            this.getTrainingMomentCreatedDate(this.training).isSame(this.moment(), 'day')))
      )
    },
    isCreator() {
      if (!this.training) {
        return false
      }
      return this.$store.getters.IS_HIMSELF(this.training.doctor_id)
    },
    datePlan() {
      if (!this.localTraining) {
        return '-'
      }
      return this.moment(this.localTraining.date_plan).format(DATE_FORMAT[`${this.$store.getters.LOCALE}`])
    },
    currentDate() {
      return this.moment().format('YYYY-MM-DD')
    },
    description() {
      return this.localTraining.description ? this.localTraining.description : '—'
    },
    minValue() {
      if (this.duration.unit == 1) return 5
      else return 0.1
    },
    maxValue() {
      if (this.duration.unit == 1) return 1440
      else return 24
    }
  },
  watch: {
    'localTraining.date_plan': {
      handler() {
        this.localTraining.date_start = this.localTraining.date_plan
      }
    },
    localIs6MWT() {
      this.changeTrainSettingsAccordingToType()
    },
    localIsRoutine() {
      this.changeTrainSettingsAccordingToType()
    }
  },
  created() {
    this.localTraining = Object.assign({}, this.training)
    this.localIs6MWT = this.localTraining.training_type === TRAINING_TYPE.sixmwt
    this.localIsRoutine = !this.localIs6MWT
    this.localWatchHr = Boolean(this.localTraining.heart_rate_max && this.localTraining.heart_rate_min)
    this.localWatchDuration = Boolean(this.localTraining.duration)
    this.heart_rate_max = this.localTraining.heart_rate_max
    this.heart_rate_min = this.localTraining.heart_rate_min
    this.duration = this.getDurationWithUnit(
      this.localTraining.duration,
      this.localTraining.course.training_duration_unit
    )
  },
  methods: {
    submit() {
      if (!this.$v.duration.value.$invalid) {
        if (!this.localWatchHr) {
          this.localTraining.heart_rate_min = null
          this.localTraining.heart_rate_max = null
        } else {
          this.localTraining.heart_rate_min = this.heart_rate_min
          this.localTraining.heart_rate_max = this.heart_rate_max
        }
        if (!this.localWatchDuration) {
          this.localTraining.duration = null
        } else {
          this.localTraining.duration = this.getDurationInMinutes(this.duration.value, this.duration.unit)
        }
        this.watchInPause = true
        this.localIs6MWT = this.localTraining.training_type === TRAINING_TYPE.sixmwt
        this.localIsRoutine = !this.localIs6MWT

        this.$emit('save', this.localTraining)
      }
    },
    changeRate(min, max) {
      this.heart_rate_min = min
      this.heart_rate_max = max
    },
    changeDurationUnit(unit) {
      this.duration.unit = unit
    },
    getType() {
      if (this.duration.unit === 1) {
        return numeric
      } else return decimal
    },
    setInterval() {
      const targetHr = this.maxHr * 0.75
      let diffHr = targetHr * 0.1
      diffHr = diffHr < 10 ? 10 : Math.round(diffHr)
      const startInt = Math.round(targetHr - diffHr)
      const endInt = Math.round(targetHr + diffHr)
      this.heart_rate_min = startInt
      this.heart_rate_max = endInt
      this.$refs.hr.changeStartValue(startInt)
      this.$refs.hr.changeEndValue(endInt)
    },
    changeTrainSettingsAccordingToType() {
      if (!this.watchInPause) {
        if (this.localIsRoutine) {
          this.localTraining.training_type = TRAINING_TYPE.base

          this.duration = this.getDurationWithUnit(
            this.localTraining.duration,
            this.localTraining.course.training_duration_unit
          )
          this.localTraining.description =
            this.training.training_type == TRAINING_TYPE.base
              ? this.training.description
              : TRAINING_DEFAULT_COMMENTS.base
        } else if (this.localIs6MWT) {
          this.localTraining.training_type = TRAINING_TYPE.sixmwt
          this.duration.value = 6
          this.duration.unit = 1
          this.localTraining.description = TRAINING_DEFAULT_COMMENTS.sixmwt
        }
      } else this.watchInPause = false
    }
  }
}
</script>
